/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import './DonationPromo.scss';

import * as React from 'react';
import cx from 'classnames';
import axios from 'axios';
import {
    Icon,
    Button,
    Heading,
    Stack,
    useBreakpoint,
    SecurityHelper,
    announce,
    Text,
} from 'sarsaparilla';

import { DonationModal } from 'ui-cart';
import { authModalActions } from '../../auth';
import { useTypedDispatch } from '../../redux';
import { DonationHeartImage } from './images/DonationHeartImage';
import { DonatePosterImage } from './images/DonatePosterImage';

export interface DonationSettings {
    is_donatable: boolean;
    asset_id: string;
    agency_id: string;
    asset_name: string;
    name: string;
    facility_id: string;
    agency_name: string;
}

export interface DonationPromoProps {
    /** Facility ID **/
    facilityID?: string;
    /** Add and additional class to the existing classes */
    className?: string;
    /** Modal Source **/
    modalSource: string;
    /** Show or hide the donation heart graphic **/
    showGraphic: boolean;
}

export function DonationPromo({
    showGraphic = true,
    facilityID = '',
    className,
    modalSource = 'unknown-promo',
}: DonationPromoProps) {
    const [donationSetting, setDonationSettings] = React.useState<DonationSettings>({
        is_donatable: false,
        asset_id: '',
        agency_id: '',
        asset_name: '',
        name: 'This location',
        facility_id: '',
        agency_name: '',
    });
    const [modalOpen, setModalOpen] = React.useState<boolean>(false);

    const dispatch = useTypedDispatch();

    const fetchDonationSettings = React.useCallback(async () => {
        if (facilityID) {
            try {
                const url = `${process.env.API}/donation/verify/${facilityID}`;
                const response = await axios.get(url, {
                    headers: {
                        Authorization: SecurityHelper.getAuthHeader(),
                    },
                });

                if (response?.data?.[facilityID]) {
                    setDonationSettings(response.data[facilityID]);
                }
            } catch {
                // eslint-disable-next-line
            }
        }
    }, [facilityID, setDonationSettings]);

    const handleButtonClick = () => {
        if (!SecurityHelper.isLoggedIn() && !SecurityHelper.isGuest()) {
            dispatch(authModalActions.openLoginWithGuestModal());
            return;
        }

        announce('Now opening the donation information form.');
        setModalOpen(true);
    };

    React.useEffect(() => {
        fetchDonationSettings();
    }, [fetchDonationSettings]);

    const breakpoint = useBreakpoint();

    const showHeart = showGraphic && breakpoint.isMdAndUp;

    const extraClassName = showHeart
        ? 'shared-donation-stack'
        : 'shared-donation-stack-small';

    const assetName = donationSetting.name ? donationSetting.name : 'This location';

    const donationBody = () => {
        return (
            <div className={cx('shared-donation-promo-inner-wrap')}>
                <div className="shared-donation-promo-text-block">
                    <div className="left">
                        <div className="mb-1 mb-sm-0 mr-3">
                            <DonatePosterImage />
                        </div>
                        <Stack space="xs" className={extraClassName}>
                            <Heading
                                headingLevel={2}
                                appearance="h5"
                                style={{ color: 'white' }}
                            >
                                Give Back to {assetName}
                            </Heading>
                            <Text color="white">
                                {assetName} accepts donations. Visitors’ donations help
                                sustain public lands’ health, diversity, and productivity.
                            </Text>
                            <div>
                                <Button
                                    appearance="tertiary-white"
                                    size="xs"
                                    onClick={handleButtonClick}
                                    aria-label={`Donate to ${assetName}`}
                                    iconAfterElement={
                                        <Icon
                                            iconName="arrow-forward"
                                            className="svg-white"
                                        />
                                    }
                                >
                                    Donate Here
                                </Button>
                            </div>
                        </Stack>
                    </div>
                    {showHeart && (
                        <div className="shared-donation-promo-phone-block">
                            <DonationHeartImage />
                        </div>
                    )}
                </div>
                <DonationModal
                    facilityID={donationSetting.facility_id}
                    agencyID={donationSetting.agency_id}
                    assetID={donationSetting.asset_id}
                    assetName={donationSetting.asset_name}
                    agencyName={donationSetting.agency_name}
                    modalSource={modalSource}
                    isOpen={modalOpen}
                    onRequestClose={() => {
                        announce('Now closing the donation information form.');
                        setModalOpen(false);
                    }}
                />
            </div>
        );
    };

    if (!facilityID || !donationSetting.is_donatable) {
        return null;
    }

    return (
        <div className={cx(className, 'shared-donation-promo-wrap')}>
            {donationBody()}
        </div>
    );
}

// cSpell:ignore donatable
